import getDistanceBetweenCoordinates from "utils/getDistanceBetweenCoordinates";
import parsePrice from "utils/parsePriceString";

/**
 * Sorts an array of markers by distance from a target marker, and then by price.
 * 
 * @param {Object} a - The first marker object to compare.
 * @param {Object} b - The second marker object to compare.
 * @returns {number} A negative value if a should come before b,
 * a positive value if a should come after b, or zero if they are equal.
 */
const sortByDistanceAndPrice = (a, b) => {
  if (a.distance === b.distance) {
    return a.price - b.price;
  }
  return a.distance - b.distance;
};

/**
 * Finds the nearby pins within a specified maximum distance from a target pin.
 *
 * @param {Marker} targetPin - The target pin to find nearby pins from.
 * @param {Array<Marker>} markers - The array of marker objects to search.
 * @param {number} radiusInMiles - The maximum distance in miles to include pins.
 * @param {Object} coordinates - The bounding coordinates to filter pins.
 * @param {number} pagination - The number of pins to return.
 * @returns {Array<Marker>} The array of nearby pins sorted by distance from the target pin.
 */
export default function findNearbyPins(targetPin, markers, coordinates, pagination) {

  // Ensure the targetPin is the first element
  const targetPinWithDistance = {
    ...targetPin,
    distance: 0 // Distance to itself is 0
  };

  if (pagination === 1) {
    return [targetPinWithDistance];
  }

  // Filter out the targetPin from the markers array
  const filteredMarkers = markers.filter(pin => pin.id !== targetPin.id);
  const { east, north, south, west } = coordinates;

  // Filter markers within the specified coordinates
  const markersWithinCoordinates = filteredMarkers.filter(pin => 
    parseFloat(pin.latitude) <= north &&
    parseFloat(pin.latitude) >= south &&
    parseFloat(pin.longitude) <= east &&
    parseFloat(pin.longitude) >= west
  );

  // Calculate distances between itself and the target pin for all markers within coordinates
  const nearbyMarkers = markersWithinCoordinates.map(pin => ({
    ...pin,
    distance: getDistanceBetweenCoordinates(
      parseFloat(targetPin.markerPosition.lat),
      parseFloat(targetPin.markerPosition.lng),
      parseFloat(pin.latitude),
      parseFloat(pin.longitude)
    ),
    price: parsePrice(pin.price) // Convert priceLow to a number
  }));

  // Separate markers into basic and paid groups
  const basicMarkers = nearbyMarkers.filter(pin => pin.isBasic);
  const paidMarkers = nearbyMarkers.filter(pin => !pin.isBasic);

  const sortedBasicMarkers = basicMarkers.sort(sortByDistanceAndPrice);
  const sortedPaidMarkers = paidMarkers.sort(sortByDistanceAndPrice);

  // Combine the sorted groups
  const sortedMarkers = [...sortedPaidMarkers, ...sortedBasicMarkers];

  return [targetPinWithDistance, ...sortedMarkers].slice(0, pagination);
}